import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Bell } from 'lucide-react';
import { dispatchFetch, T, F, bd } from '@/lib/dispatchApi';
import DispatchButton from '@/components/dispatch/DispatchButton';

// The bell on Today counts unread notifications, so this page lists them.
// It used to show on/off switches that were never saved anywhere.
function fmtWhen(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString('en-CA', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
}

export default function DispatchNotificationsPage() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [marking, setMarking] = useState(false);

  const load = useCallback(async () => {
    setError(false);
    setLoading(true);
    try {
      const res = await dispatchFetch('/api/notifications');
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setItems(Array.isArray(data) ? data : []);
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const unread = items.filter((n) => !n.read).length;

  const markAllRead = async () => {
    setMarking(true);
    try {
      const res = await dispatchFetch('/api/notifications/mark-all-read', { method: 'POST' });
      if (res.ok) setItems((prev) => prev.map((n) => ({ ...n, read: true, is_read: true })));
    } finally {
      setMarking(false);
    }
  };

  const open = (n) => {
    if (!n.read) {
      setItems((prev) => prev.map((x) => (x.id === n.id ? { ...x, read: true, is_read: true } : x)));
      dispatchFetch(`/api/notifications/${n.id}/read`, { method: 'POST' }).catch(() => {});
    }
    const jobId = n.data?.job_id;
    if (jobId) navigate(`/dispatch/job/${jobId}`);
  };

  return (
    <div style={{ backgroundColor: T.night, minHeight: '100vh', color: T.moon, fontFamily: F.heading }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 20px 10px 8px', borderBottom: `1px solid ${T.nightHair}` }}>
        <button
          type="button"
          aria-label="Back"
          onClick={() => navigate(-1)}
          style={{ background: 'none', border: 'none', width: 44, height: 44, padding: 0, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
        >
          <ArrowLeft size={18} color={T.moonMute} strokeWidth={1.5} />
        </button>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1 }}>
          <Bell size={15} color={T.moonMute} strokeWidth={1.5} />
          <span style={{ fontSize: 15, fontWeight: 500, color: T.moon }}>Notifications</span>
        </div>
      </div>

      <div style={{ padding: '24px 20px' }}>
        <div style={{ marginBottom: 20 }}>
          {error ? (
            <DispatchButton variant="main" data-testid="notifications-retry" onClick={load}>Try again</DispatchButton>
          ) : (
            <DispatchButton variant="main" data-testid="notifications-mark-read" loading={marking} disabled={loading || unread === 0} onClick={markAllRead}>
              {unread > 0 ? `Mark ${unread} as read` : 'All caught up'}
            </DispatchButton>
          )}
        </div>

        {error && <p style={{ fontSize: 13, color: T.moonFaint }}>Notifications didn't load.</p>}

        {!error && !loading && items.length === 0 && (
          <p style={{ fontSize: 13, color: T.moonFaint, paddingTop: 20, textAlign: 'center' }}>No notifications yet.</p>
        )}

        {items.length > 0 && (
          <div style={{ backgroundColor: T.nightSoft, ...bd(T.nightHair), borderRadius: 2 }}>
            {items.map((n, i) => (
              <div
                key={n.id}
                className="bx-tap"
                role="button"
                tabIndex={0}
                onClick={() => open(n)}
                onKeyDown={(e) => { if (e.key === 'Enter') open(n); }}
                style={{ display: 'flex', gap: 12, padding: '14px 16px', minHeight: 44, cursor: 'pointer', ...(i < items.length - 1 ? { borderBottom: `1px solid ${T.nightHair}` } : {}) }}
              >
                <div aria-hidden="true" style={{ width: 7, height: 7, borderRadius: 4, marginTop: 6, flexShrink: 0, backgroundColor: n.read ? 'transparent' : T.moon }} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <p style={{ fontSize: 14, color: n.read ? T.moonSoft : T.moon, fontWeight: n.read ? 400 : 500, margin: '0 0 2px 0', overflowWrap: 'anywhere' }}>{n.title || 'Update'}</p>
                  {n.body && <p style={{ fontSize: 12, color: T.moonFaint, margin: '0 0 4px 0', overflowWrap: 'anywhere' }}>{n.body}</p>}
                  <p style={{ fontFamily: F.mono, fontSize: 11, color: T.moonFaint, margin: 0 }}>{fmtWhen(n.created_at)}</p>
                </div>
              </div>
            ))}
          </div>
        )}

        <p style={{ fontSize: 12, color: T.moonFaint, textAlign: 'center', marginTop: 20 }}>
          Push notifications are managed by your device settings.
        </p>
      </div>
    </div>
  );
}
